/**
 * Check-in: zgjedh lokacion → bën foto → dërgon check-in.
 * Lista e check-in-eve të kaluara: web (mini foto + lightbox), mobile (kartë e ngushtë, Data/Ora).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

import { BACKEND_URL } from '../config';

/** URL për foto: përdor gjithmonë BACKEND_URL për path relative që fotot të ngarkohen nga serveri API (web + mobile). */
const getPhotoFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  const base = (BACKEND_URL && BACKEND_URL.startsWith('http'))
    ? BACKEND_URL.replace(/\/$/, '')
    : (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}${path}`;
};

const Checkin = () => {
  const { t } = useLanguage();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [checkins, setCheckins] = useState([]);
  const [loadingCheckins, setLoadingCheckins] = useState(false);
  const [step, setStep] = useState(1); // 1: lokacion, 2: kamera, 3: konfirmo
  const [stream, setStream] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [photoLoadErrors, setPhotoLoadErrors] = useState(new Set()); // checkin id ku foto 404
  const [lightboxPhoto, setLightboxPhoto] = useState(null); // { url } për web lightbox
  const [checkoutActive, setCheckoutActive] = useState(false);
  const [checkoutComment, setCheckoutComment] = useState('');
  const [fuelModal, setFuelModal] = useState(null);
  const [fuelLitersIn, setFuelLitersIn] = useState('');
  const [fuelPartsIn, setFuelPartsIn] = useState('');
  const [fuelCommentIn, setFuelCommentIn] = useState('');
  const [savingFuel, setSavingFuel] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (lightboxPhoto) setLightboxPhoto(null);
        else if (fuelModal) setFuelModal(null);
        else navigate('/me');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxPhoto, fuelModal, navigate]);

  const getToken = () => localStorage.getItem('access_token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/s475/devices`, { headers: headers() });
      const list = Array.isArray(res.data) ? res.data : [];
      setLocations(list);
      return list;
    } catch (e) {
      console.error('fetchLocations', e);
      toast.error(t('error') || 'Gabim në ngarkimin e lokacioneve');
      return [];
    } finally {
      setLoadingLocations(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const fetchCheckins = useCallback(async () => {
    setLoadingCheckins(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/checkins`, { headers: headers() });
      setCheckins(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('fetchCheckins', e);
      setCheckins([]);
    } finally {
      setLoadingCheckins(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchCheckins();
  }, [fetchLocations, fetchCheckins]);

  const startCamera = useCallback(async () => {
    if (!selectedLocation) return;
    setCameraError(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      setStream(mediaStream);
      setStep(2);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = mediaStream;
      }, 100);
    } catch (err) {
      console.error('Camera error', err);
      setCameraError(err.message || 'Kamera nuk u hap');
      toast.error('Nuk mund të hapet kamera. Kontrolloni lejet.');
    }
  }, [selectedLocation]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCapturedBlob(null);
    setStep(1);
    setCheckoutActive(false);
    setCheckoutComment('');
  }, [stream]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !stream) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          setCapturedBlob(blob);
          setStep(3);
          stream.getTracks().forEach((track) => track.stop());
          setStream(null);
          if (videoRef.current) videoRef.current.srcObject = null;
        }
      },
      'image/jpeg',
      0.9
    );
  }, [stream]);

  const submitCheckin = useCallback(async () => {
    if (!selectedLocation || !capturedBlob) return;
    if (checkoutActive && !checkoutComment.trim()) {
      toast.error(t('checkoutCommentRequired'));
      return;
    }
    setSubmitting(true);
    try {
      let userLat = null;
      let userLon = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            });
          });
          if (pos?.coords?.latitude != null && pos?.coords?.longitude != null) {
            userLat = pos.coords.latitude;
            userLon = pos.coords.longitude;
          }
        } catch (err) {
          // Vazhdojmë pa koordinata; backend kërkon pozicion vetëm nëse lokacioni ka rrezë
        }
      }
      const formData = new FormData();
      formData.append('location_id', selectedLocation.id);
      formData.append('file', capturedBlob, checkoutActive ? 'checkout.jpg' : 'checkin.jpg');
      formData.append('event_kind', checkoutActive ? 'checkout' : 'checkin');
      if (checkoutActive) {
        formData.append('work_comment', checkoutComment.trim());
      }
      if (userLat != null && userLon != null) {
        formData.append('user_lat', String(userLat));
        formData.append('user_lon', String(userLon));
      }
      await axios.post(`${BACKEND_URL}/api/checkins`, formData, {
        headers: {
          ...headers(),
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(checkoutActive ? t('checkoutSuccess') : t('checkinSuccessToast'));
      setCapturedBlob(null);
      setSelectedLocation(null);
      setStep(1);
      setCheckoutActive(false);
      setCheckoutComment('');
      fetchCheckins();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Gabim';
      toast.error(typeof msg === 'string' ? msg : t('checkinFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [selectedLocation, capturedBlob, fetchCheckins, checkoutActive, checkoutComment, t]);

  const openFuelModal = useCallback((c) => {
    setFuelModal(c);
    const fl = c.fuel_liters;
    setFuelLitersIn(fl != null && fl !== '' ? String(fl).replace('.', ',') : '');
    const pc = c.parts_count;
    setFuelPartsIn(pc != null && pc !== '' ? String(pc) : '');
    setFuelCommentIn(c.work_comment != null ? String(c.work_comment) : '');
  }, []);

  const saveFuel = useCallback(async () => {
    if (!fuelModal?.id) return;
    const litersRaw = fuelLitersIn.trim();
    const partsRaw = fuelPartsIn.trim();
    let fuel_liters = null;
    if (litersRaw !== '') {
      const v = parseFloat(litersRaw.replace(',', '.'));
      if (Number.isNaN(v) || v < 0) {
        toast.error(t('fuelInvalidLiters'));
        return;
      }
      fuel_liters = v;
    }
    let parts_count = null;
    if (partsRaw !== '') {
      const n = Number(partsRaw.replace(',', '.'));
      if (Number.isNaN(n) || n < 0 || !Number.isInteger(n)) {
        toast.error(t('fuelInvalidParts'));
        return;
      }
      parts_count = n;
    }
    setSavingFuel(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/checkins/${fuelModal.id}`,
        {
          fuel_liters,
          parts_count,
          work_comment: fuelCommentIn.trim() || null,
        },
        { headers: headers() }
      );
      toast.success(t('fuelSavedToast'));
      setFuelModal(null);
      fetchCheckins();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : t('errorSaving'));
    } finally {
      setSavingFuel(false);
    }
  }, [fuelModal, fuelLitersIn, fuelPartsIn, fuelCommentIn, fetchCheckins, t]);

  const cancelPhoto = useCallback(() => {
    setCapturedBlob(null);
    setStep(2);
    startCamera();
  }, [startCamera]);

  const photoUrl = capturedBlob ? URL.createObjectURL(capturedBlob) : null;

  const formatDate = (iso) => {
    if (!iso) return '–';
    try {
      const d = new Date(iso);
      return d.toLocaleString('sq-AL', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  /** Data : 09.03.2023 Ora : 11:46 (mobile: pa "Data :", vetëm data + Ora) */
  const formatDateDataOra = (iso, forMobile = false) => {
    if (!iso) return forMobile ? '– Ora : –' : 'Data : – Ora : –';
    try {
      const d = new Date(iso);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const dateStr = `${day}.${month}.${year}`;
      const timeStr = `${hours}:${minutes}`;
      return forMobile ? `${dateStr} Ora : ${timeStr}` : `Data : ${dateStr} Ora : ${timeStr}`;
    } catch {
      return forMobile ? '– Ora : –' : 'Data : – Ora : –';
    }
  };

  const lastCheckinsToShow = isMobile ? checkins.slice(0, 5) : checkins.slice(0, 10);
  const isWebStep1 = !isMobile && step === 1;
  const canSeeCheckinPhotos = hasRole('SUPERADMIN') || hasRole('ADMIN');

  const getVisitWorkCommentForLocation = useCallback(
    (loc) => {
      if (!loc?.id) return '';
      const locId = String(loc.id);
      const atLoc = checkins.filter((x) => String(x.location_id || '') === locId);
      const visitCheckin = atLoc.find((x) => (x.event_kind || 'checkin') === 'checkin');
      const w = visitCheckin?.work_comment;
      return w != null && String(w).trim() ? String(w).trim() : '';
    },
    [checkins]
  );

  const plusFuelButtonClass =
    'w-7 h-7 rounded-full border-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 text-base font-bold leading-none flex items-center justify-center hover:bg-blue-50 dark:hover:bg-blue-900/20 shrink-0';

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-6">
        <div className={`mx-auto px-4 pt-4 md:pt-6 ${isMobile ? 'max-w-lg' : 'max-w-5xl'}`}>
          {/* Mobile: titulli + back në një rresht */}
          {isMobile && (
            <div className="flex items-center justify-between gap-3 mb-4">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{t('checkin')}</h1>
              <Link to="/me" className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm">
                <span>{t('back')}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
            </div>
          )}

          {/* Web (step 1): dropdown majtas, poshtë tabela e gjerë (10 të fundit) – një kolonë */}
          <div className={isWebStep1 ? 'flex flex-col gap-6' : ''}>
          {step === 1 && (
            <div className={isWebStep1 ? 'md:flex md:flex-col md:items-start' : ''}>
              {isWebStep1 && (
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  {t('checkin')}
                </h1>
              )}
              <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-5 mb-6 md:mb-0 ${isWebStep1 ? 'md:w-full md:max-w-md' : ''}`}>
                {checkoutActive && step === 1 ? (
                  <>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('checkoutStepTitle')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {t('locationLabel')}: <strong>{selectedLocation?.location_name || selectedLocation?.label}</strong>
                    </p>
                    <textarea
                      value={checkoutComment}
                      onChange={(e) => setCheckoutComment(e.target.value)}
                      rows={4}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                      placeholder={t('commentRepairPlaceholder')}
                    />
                    {checkoutComment.trim() && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('checkoutCommentPrefilledHint')}</p>
                    )}
                    <div className="mt-4 flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!checkoutComment.trim()) {
                            toast.error(t('checkoutCommentRequired'));
                            return;
                          }
                          startCamera();
                        }}
                        disabled={!selectedLocation}
                        className="w-full py-3 rounded-xl font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white transition"
                      >
                        {t('continueToCamera')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCheckoutActive(false);
                          setCheckoutComment('');
                        }}
                        className="w-full py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {t('selectLocationHint')}
                    </p>
                    {loadingLocations ? (
                      <p className="text-gray-500 dark:text-gray-400">{t('loadingLocations')}</p>
                    ) : locations.length === 0 ? (
                      <p className="text-amber-600 dark:text-amber-400">{t('noLocations')}</p>
                    ) : (
                      <>
                        <select
                          value={selectedLocation?.id ?? ''}
                          onChange={(e) => {
                            const id = e.target.value;
                            const loc = locations.find((l) => l.id === id) || null;
                            setSelectedLocation(loc);
                          }}
                          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">{t('selectLocationPlaceholder')}</option>
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.location_name || loc.label || loc.id}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={startCamera}
                          disabled={!selectedLocation}
                          className="mt-4 w-full py-3 rounded-xl font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white transition"
                        >
                          {t('openCameraAndPhoto')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedLocation) {
                              toast.error(t('selectLocationPlaceholder'));
                              return;
                            }
                            setCheckoutComment(getVisitWorkCommentForLocation(selectedLocation));
                            setCheckoutActive(true);
                          }}
                          disabled={!selectedLocation}
                          className="mt-2 w-full py-3 rounded-xl font-medium border-2 border-amber-500 text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50 transition"
                        >
                          {t('checkoutButton')}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Hapi 2: Kamera */}
          {step === 2 && (
            <>
              {!isMobile && (
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">{t('checkin')}</h1>
              )}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
              {checkoutActive && checkoutComment.trim() && (
                <div className="px-4 pt-4 pb-0">
                  <p className="text-xs text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-3 py-2">
                    <span className="font-medium text-gray-600 dark:text-gray-400">{t('checkoutCommentReminder')}: </span>
                    {checkoutComment.trim()}
                  </p>
                </div>
              )}
              <div className="relative aspect-[4/3] bg-black">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              {cameraError && (
                <p className="p-3 text-red-600 dark:text-red-400 text-sm">{cameraError}</p>
              )}
              <div className="p-4 flex gap-3">
                <button
                  type="button"
                  onClick={stopCamera}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  onClick={capturePhoto}
                  className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium"
                >
                  {t('capturePhoto')}
                </button>
              </div>
            </div>
            </>
          )}

          {/* Hapi 3: Parapamje foto + konfirmo */}
          {step === 3 && (
            <>
              {!isMobile && (
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">{t('checkin')}</h1>
              )}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
              <p className="p-3 text-sm text-gray-600 dark:text-gray-400">
                {t('locationLabel')}: <strong>{selectedLocation?.location_name || selectedLocation?.label}</strong>
              </p>
              {checkoutActive && (
                <p className="px-3 pb-2 text-xs text-gray-600 dark:text-gray-400">
                  <strong>{t('commentColumn')}:</strong> {checkoutComment.trim() || '—'}
                </p>
              )}
              {photoUrl && (
                <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-900">
                  <img src={photoUrl} alt="Check-in" className="w-full h-full object-contain" />
                </div>
              )}
              <div className="p-4 flex gap-3">
                <button
                  type="button"
                  onClick={cancelPhoto}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                >
                  {t('retake')}
                </button>
                <button
                  type="button"
                  onClick={submitCheckin}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-medium"
                >
                  {submitting ? t('sending') : checkoutActive ? t('checkoutButton') : t('confirmCheckin')}
                </button>
              </div>
            </div>
            </>
          )}

          {/* Lista: web = 10 të fundit poshtë (tabelë e gjerë), mobile = 5 të fundit */}
          <div className={`mt-6 ${isWebStep1 ? 'md:mt-0 md:w-full' : ''}`}>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              {isMobile ? t('lastCheckins5') : t('lastCheckins10')}
            </h2>
            {loadingCheckins ? (
              <p className="text-gray-500 dark:text-gray-400">{t('loading')}</p>
            ) : lastCheckinsToShow.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t('noCheckinsYet')}
              </p>
            ) : (
              <div className="w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div
                  className={
                    canSeeCheckinPhotos
                      ? 'grid gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-600 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide grid-cols-[minmax(0,1fr)_minmax(132px,1fr)_2.5rem_2.75rem] md:grid-cols-[minmax(0,1fr)_minmax(200px,1.2fr)_2.5rem_4rem]'
                      : 'grid grid-cols-[minmax(0,1fr)_minmax(132px,1fr)_2.75rem] md:grid-cols-[minmax(0,1fr)_minmax(200px,1.2fr)_2.75rem] gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-600 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide'
                  }
                >
                  <div>{t('locationLabel')}</div>
                  <div>{t('dateTimeColumn')}</div>
                  <div className="text-center">{t('addFuelPlusColumn')}</div>
                  {canSeeCheckinPhotos && <div className="text-center">{t('photoColumn')}</div>}
                </div>
                {lastCheckinsToShow.map((c, index) => {
                  const kind = c.event_kind || 'checkin';
                  const canEditFuel =
                    index === 0 &&
                    kind !== 'checkout' &&
                    (String(user?.id || '') === String(c.user_id || '') || hasRole('SUPERADMIN'));
                  return (
                  <div
                    key={c.id}
                    className={
                      canSeeCheckinPhotos
                        ? 'grid gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 items-center grid-cols-[minmax(0,1fr)_minmax(132px,1fr)_2.5rem_2.75rem] md:grid-cols-[minmax(0,1fr)_minmax(200px,1.2fr)_2.5rem_4rem]'
                        : 'grid grid-cols-[minmax(0,1fr)_minmax(132px,1fr)_2.75rem] md:grid-cols-[minmax(0,1fr)_minmax(200px,1.2fr)_2.75rem] gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 items-center'
                    }
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate text-sm">
                        {c.location_name || c.location_id}
                      </p>
                      {c.user_name && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{c.user_name}</p>
                      )}
                      <p className="text-[11px] mt-0.5 text-gray-500 dark:text-gray-400">
                        {kind === 'checkout' ? t('eventKindCheckout') : t('eventKindCheckin')}
                      </p>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 min-w-0 whitespace-nowrap">
                      {formatDateDataOra(c.created_at, isMobile)}
                    </div>
                    <div className="flex justify-center shrink-0">
                      {canEditFuel ? (
                        <button
                          type="button"
                          onClick={() => openFuelModal(c)}
                          className={plusFuelButtonClass}
                          aria-label={t('addFuelAria')}
                        >
                          +
                        </button>
                      ) : (
                        <span className="w-7 h-7 flex items-center justify-center text-gray-300 dark:text-gray-600 text-xs" aria-hidden>
                          –
                        </span>
                      )}
                    </div>
                    {canSeeCheckinPhotos && (
                      <div className="flex justify-center shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            if (c.photo_url && !photoLoadErrors.has(c.id)) {
                              setLightboxPhoto(getPhotoFullUrl(c.photo_url));
                            }
                          }}
                          className="w-10 h-10 md:w-14 md:h-14 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden cursor-pointer hover:opacity-90 hover:ring-2 hover:ring-blue-400 transition flex items-center justify-center shrink-0"
                          aria-label={t('viewPhotoLarge')}
                        >
                          {c.photo_url && !photoLoadErrors.has(c.id) ? (
                            <img
                              src={getPhotoFullUrl(c.photo_url)}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={() => setPhotoLoadErrors((prev) => new Set(prev).add(c.id))}
                            />
                          ) : (
                            <span className="text-gray-400 text-sm">–</span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {fuelModal && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fuel-modal-title"
          onClick={() => !savingFuel && setFuelModal(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 w-full max-w-md p-4 md:p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="fuel-modal-title" className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              {t('fuelModalTitle')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 truncate">
              {fuelModal.location_name || fuelModal.location_id} · {formatDateDataOra(fuelModal.created_at, true)}
            </p>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('litersFuelPlaceholder')}</label>
            <input
              type="text"
              inputMode="decimal"
              value={fuelLitersIn}
              onChange={(e) => setFuelLitersIn(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              placeholder="0"
            />
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('productFieldLabel')}</label>
            <input
              type="text"
              inputMode="numeric"
              value={fuelPartsIn}
              onChange={(e) => setFuelPartsIn(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              placeholder="0"
            />
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('fuelModalCommentOptional')}</label>
            <textarea
              value={fuelCommentIn}
              onChange={(e) => setFuelCommentIn(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4 resize-y min-h-[4.5rem]"
            />
            <div className="flex gap-3">
              <button
                type="button"
                disabled={savingFuel}
                onClick={() => setFuelModal(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                disabled={savingFuel}
                onClick={saveFuel}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium text-sm"
              >
                {savingFuel ? t('sending') : t('saveFuelButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox web: foto e madhe, mbyll me klik mbi overlay, butonin X ose ESC */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Foto check-in"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxPhoto(null); }}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-gray-900 flex items-center justify-center text-2xl font-bold z-20 border-2 border-gray-300"
            aria-label={t('close')}
          >
            ×
          </button>
          <img
            src={lightboxPhoto}
            alt="Check-in"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded shadow-2xl z-10"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Layout>
  );
};

export default Checkin;
